// Get the API key from environment variables
const FASHN_API_KEY = process.env.REACT_APP_FASHN_API_KEY;
const FASHN_API_BASE_URL = "https://api.fashn.ai/nightly";

// Interface for try-on request
export interface TryOnRequest {
	modelImage: string; // URL or base64 of the person's image
	garmentImage: string; // URL or base64 of the clothing item
	category?: "auto" | "tops" | "bottoms" | "one-pieces";
	segmentationFree?: boolean;
	mode?: "performance" | "balanced" | "quality";
}

// Interface for FASHN API error object
export interface FashnError {
	name: string; // Error type like 'ImageLoadError', 'PoseError', etc.
	message: string; // Detailed error message
}

// Interface for try-on response
export interface TryOnResponse {
	id: string;
	status: "starting" | "in_queue" | "processing" | "completed" | "failed";
	output?: string[];
	error?: FashnError;
}

// Class to handle FASHN.AI API calls
export class FashnAIService {
	private apiKey: string;

	constructor(apiKey?: string) {
		if (!apiKey) {
			throw new Error("API key is not set");
		}
		this.apiKey = apiKey;
	}

	// Get a user-friendly error message based on error code or name
	getReadableErrorMessage(error: any): string {
		// For HTTP errors (status codes)
		if (typeof error === 'string') {
			if (error.includes('401') || error.includes('403')) {
				return "Authentication failed. Please check your API key.";
			}
			if (error.includes('429') && error.includes('RateLimitExceeded')) {
				return "Too many requests. Please try again in a moment.";
			}
			if (error.includes('429') && error.includes('OutOfCredits')) {
				return "You've used all your API credits. Please purchase more.";
			}
			if (error.includes('500')) {
				return "Server error. Please try again later.";
			}
		}
		
		// For runtime errors (object with name property)
		if (error && typeof error === 'object' && error.name) {
			switch(error.name) {
				case 'ImageLoadError':
					return "Failed to load one of the images. Please try different images.";
				case 'ContentModerationError':
					return "Content moderation detected prohibited content in one of the images.";
				case 'PhotoTypeError':
					return "Couldn't determine the garment type. Please try a clearer garment image.";
				case 'PoseError':
					return "Couldn't detect a person in the model image. Please use a photo where the person is clearly visible.";
				case 'PipelineError':
					return "Processing error. Please try again with different images.";
				default:
					return error.message || "An unknown error occurred.";
			}
		}
		
		return "An error occurred. Please try again.";
	}

	// Initiate a try-on request
	async startTryOn(request: TryOnRequest): Promise<string> {
		try {
			const response = await fetch(`${FASHN_API_BASE_URL}/run`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${this.apiKey}`,
				},
				body: JSON.stringify({
					model_image: request.modelImage,
					garment_image: request.garmentImage,
					category: request.category ?? "auto",
					segmentation_free: request.segmentationFree ?? true,
					mode: request.mode ?? "balanced",
				}),
			});

			if (!response.ok) {
				const errorText = await response.text();
				// Check for specific status codes
				if (response.status === 401 || response.status === 403) {
					throw new Error(this.getReadableErrorMessage(`${response.status} - Authentication failed`));
				}
				if (response.status === 429) {
					const errorType = errorText.includes('credits') ? 'OutOfCredits' : 'RateLimitExceeded';
					throw new Error(this.getReadableErrorMessage(`${response.status} - ${errorType}`));
				}
				throw new Error(this.getReadableErrorMessage(`API error: ${response.status} - ${errorText}`));
			}

			const data = await response.json();

			if (data.error) {
				throw new Error(this.getReadableErrorMessage(data.error));
			}

			return data.id;
		} catch (error) {
			console.error("Error starting try-on:", error);
			throw error;
		}
	}

	// Check the status of a try-on request
	async checkTryOnStatus(id: string): Promise<TryOnResponse> {
		try {
			const response = await fetch(`${FASHN_API_BASE_URL}/status/${id}`, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${this.apiKey}`,
				},
			});

			if (!response.ok) {
				const errorText = await response.text();
				if (response.status === 401 || response.status === 403) {
					throw new Error(this.getReadableErrorMessage(`${response.status} - Authentication failed`));
				}
				throw new Error(this.getReadableErrorMessage(`API error: ${response.status} - ${errorText}`));
			}

			const data = await response.json();
			return data;
		} catch (error) {
			console.error("Error checking try-on status:", error);
			throw error;
		}
	}
}

// Export a singleton instance
export const fashnai = new FashnAIService(FASHN_API_KEY);
